import { Button, Callout, Modal } from '@dev-spendesk/grapes';
import React, { useEffect, useState } from 'react';

import type { CostCenter } from 'modules/budgets/models/costCenter';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { useAccountingSoftwareName } from 'src/core/modules/accounting-integration/apis';
import { type AnalyticalFieldExportSettings } from 'src/core/modules/accounting-integration/apis/useAnalyticalFieldExportSettings';
import { type SyncMode } from 'src/core/modules/accounting-integration/apis/useSetSyncSettings';
import { AnalyticalFieldValueMappingSelector } from 'src/core/modules/accounting-integration/components/AnalyticalFieldMapping/AnalyticalFieldValueMappingSelector';
import { type SaveChanges } from 'src/core/modules/accounting-integration/hooks/useAnalyticalFieldMapping';
import {
  type AnalyticalFieldValueMappingV2,
  type AnalyticalFieldMappingV2,
} from 'src/core/modules/bookkeep';

import { EcFormModalContent } from './EcFormModalContent';
import { EcFormModalContentPullMode } from './EcFormModalContentPullMode';
import { EcFormModalContentWithSync } from './EcFormModalContentWithSync';
import { type FormValues } from './formValues';
import { useEcFormikProps } from './getFormikProps';
import { type ExpenseCategory } from '../../expenseCategory';

type Props = {
  expenseCategory?: ExpenseCategory;
  expenseCategoryNames: string[];
  costCenters: CostCenter[];
  isOpen: boolean;
  isMapSyncEnabled: boolean;
  isSplittable: boolean;
  onSubmit(values: FormValues): Promise<void>;
  onCancel(): void;
  designatedCustomFieldId?: string | null;
  analyticalFieldMappings: AnalyticalFieldMappingV2[];
  fieldMapping: AnalyticalFieldMappingV2 | undefined;
  setFieldMapping: React.Dispatch<
    React.SetStateAction<AnalyticalFieldMappingV2 | undefined>
  >;
  onSaveChanges: (arguments_: SaveChanges) => void;
  valueMapping?: AnalyticalFieldValueMappingV2;
  valueInDatabase?: AnalyticalFieldValueMappingV2;
  setValueMapping: React.Dispatch<
    React.SetStateAction<AnalyticalFieldValueMappingV2 | undefined>
  >;
  initialSyncMode?: SyncMode;
};

type Stage = 'sync' | 'manualValueCreation' | 'pull' | 'pullConfirm';
type SyncErrors = 'missingSyncMode' | 'missingSelectedField';

export const EcFormModal = ({
  designatedCustomFieldId,
  expenseCategory,
  expenseCategoryNames,
  costCenters,
  isOpen,
  isMapSyncEnabled,
  isSplittable,
  onSubmit,
  onCancel,
  analyticalFieldMappings,
  fieldMapping,
  setFieldMapping,
  onSaveChanges,
  setValueMapping,
  valueMapping,
  initialSyncMode,
  valueInDatabase,
}: Props) => {
  const { t } = useTranslation('global');
  const { accountingSoftwareName } = useAccountingSoftwareName();
  const [syncMode, setSyncMode] = useState<SyncMode | undefined>(undefined);
  const [exportSettings, setExportSettings] = useState<
    AnalyticalFieldExportSettings | undefined
  >(undefined);
  const [stage, setStage] = useState<Stage>(
    isMapSyncEnabled && !expenseCategoryNames.length
      ? 'sync'
      : 'manualValueCreation',
  );

  const [error, setError] = useState<SyncErrors | null>(null);

  useEffect(() => {
    if (!isMapSyncEnabled || syncMode === 'pull') {
      return;
    }

    if (
      expenseCategoryNames.length === 0 &&
      (!fieldMapping?.internalId ||
        fieldMapping?.internalId !== designatedCustomFieldId)
    ) {
      if (!isOpen) {
        setTimeout(() => {
          setStage('sync');
        }, 1000);
        return;
      }

      setStage('sync');
      return;
    }

    if (
      fieldMapping?.internalId &&
      fieldMapping.internalId === designatedCustomFieldId
    ) {
      setStage('manualValueCreation');
    }
  }, [
    expenseCategoryNames,
    fieldMapping,
    designatedCustomFieldId,
    isMapSyncEnabled,
    syncMode,
    isOpen,
  ]);

  const formikProps = useEcFormikProps({
    costCenters,
    expenseCategory,
    onSubmit,
    expenseCategoryNames,
  });

  useEffect(() => {
    formikProps.resetForm();
  }, [isOpen]);

  const handleSyncMode = () => {
    if (!syncMode) {
      setError('missingSyncMode');
      return;
    }

    switch (syncMode) {
      case 'none':
        setStage('manualValueCreation');
        break;
      case 'pull':
        setStage('pull');
        break;
      default:
        setError('missingSyncMode');
        break;
    }
  };

  const handleFieldSelect = () => {
    if (!fieldMapping) {
      setError('missingSelectedField');
      return;
    }

    const mapping = {
      fieldId: fieldMapping.id,
      fieldType: 'expenseCategory' as const,
      internalId: designatedCustomFieldId || null,
    };

    onSaveChanges({
      mapping,
      syncMode: 'pull',
      exportSettings,
    });

    setStage('pullConfirm');
  };

  const handleSubmit = () => {
    setError(null);

    switch (stage) {
      case 'manualValueCreation':
        formikProps.submitForm();
        break;
      case 'sync':
        handleSyncMode();
        break;
      case 'pull':
        handleFieldSelect();
        break;
      case 'pullConfirm':
        handleCancel();
        break;
      default:
        break;
    }
  };

  const handleCancel = () => {
    formikProps.resetForm();
    setSyncMode(undefined);
    onCancel();

    if (fieldMapping?.internalId === designatedCustomFieldId) {
      return;
    }

    if (isMapSyncEnabled && !expenseCategoryNames.length) {
      setStage('sync');
    }
  };

  const handleBack = () => {
    if (stage === 'pull') {
      setStage('sync');
    }

    if (stage === 'manualValueCreation') {
      setStage('sync');
    }
  };

  const getModalTitle = (): string => {
    switch (stage) {
      case 'manualValueCreation':
        return t('expenseCategories.form.creationTitle');
      case 'sync':
        return t('expenseCategories.form.editTitle');
      case 'pull':
        return t('expenseCategories.mapSync.pullTitle', {
          integration: accountingSoftwareName,
        });
      case 'pullConfirm':
        return t('expenseCategories.mapSync.pullConfirmTitle', {
          integration: accountingSoftwareName,
        });
      default:
        return '';
    }
  };

  const getModalSubtitle = (): string => {
    switch (stage) {
      case 'pull':
        return t('expenseCategories.mapSync.pullSubtitle', {
          integration: accountingSoftwareName,
        });
      case 'pullConfirm':
        return t('expenseCategories.mapSync.pullConfirmSubtitle', {
          integration: accountingSoftwareName,
        });
      default:
        return '';
    }
  };

  const getActionButtons = () => {
    if (stage === 'sync') {
      return [
        <Button
          key="cancel"
          variant="secondaryNeutral"
          text={t('expenseCategories.form.cancel')}
          onClick={handleCancel}
        />,
        <Button
          key="save"
          variant="primaryBrand"
          text={t('misc.continue')}
          onClick={handleSubmit}
        />,
      ];
    }

    if (stage === 'manualValueCreation') {
      const isBackButtonVisible =
        isMapSyncEnabled &&
        !fieldMapping?.internalId &&
        !expenseCategoryNames.length;

      return [
        <Button
          key="cancel"
          variant="secondaryNeutral"
          iconName={isBackButtonVisible ? 'chevron-left' : undefined}
          text={
            isBackButtonVisible
              ? t('misc.back')
              : t('expenseCategories.form.cancel')
          }
          onClick={() => (isBackButtonVisible ? handleBack() : handleCancel())}
        />,
        <Button
          key="save"
          variant="primaryBrand"
          text={t('expenseCategories.mapSync.createExpenseCategory')}
          onClick={handleSubmit}
        />,
      ];
    }

    if (stage === 'pull') {
      return [
        <Button
          key="cancel"
          variant="secondaryNeutral"
          iconName="chevron-left"
          text={t('misc.back')}
          onClick={handleBack}
        />,
        <Button
          key="save"
          variant="primaryBrand"
          text={t('expenseCategories.mapSync.startPull')}
          onClick={handleSubmit}
        />,
      ];
    }

    if (stage === 'pullConfirm') {
      return [
        <Button
          key="save"
          variant="primaryBrand"
          text={t('expenseCategories.mapSync.gotIt')}
          onClick={handleSubmit}
        />,
      ];
    }

    return [
      <Button
        key="cancel"
        variant="secondaryNeutral"
        text={t('expenseCategories.form.cancel')}
        onClick={handleCancel}
      />,
      <Button
        key="save"
        variant="primaryBrand"
        text={
          stage === 'manualValueCreation'
            ? t('expenseCategories.form.add')
            : t('expenseCategories.form.save')
        }
        onClick={handleSubmit}
      />,
    ];
  };

  return (
    <Modal
      isOpen={isOpen}
      iconName={stage === 'pullConfirm' ? 'arrow-path' : 'plus'}
      iconVariant="info"
      title={getModalTitle()}
      subtitle={getModalSubtitle()}
      actions={getActionButtons()}
    >
      {stage === 'sync' && (
        <EcFormModalContentWithSync
          syncMode={syncMode}
          onChange={setSyncMode}
        />
      )}

      {stage === 'pull' && (
        <EcFormModalContentPullMode
          exportSettings={exportSettings}
          onExportSettingsChange={(settings) => setExportSettings(settings)}
          fieldMapping={fieldMapping}
          analyticalFieldMappings={analyticalFieldMappings}
          onChange={setFieldMapping}
          syncMode={syncMode}
          isSplittable={isSplittable}
        />
      )}

      {stage === 'manualValueCreation' && (
        <>
          <EcFormModalContent costCenters={costCenters} {...formikProps} />
          {fieldMapping?.internalId && (
            <AnalyticalFieldValueMappingSelector
              selectedValue={
                valueMapping && {
                  ...valueMapping,
                  label: valueMapping.externalName,
                  key: valueMapping.id,
                }
              }
              values={fieldMapping.values}
              onValueSelected={(value) => {
                setValueMapping(value);
              }}
              syncMode={initialSyncMode}
              valueInDatabase={
                valueInDatabase && {
                  ...valueInDatabase,
                  label: valueInDatabase.externalName,
                  key: valueInDatabase.id,
                }
              }
            />
          )}
        </>
      )}

      {error && (
        <Callout
          className="mt-16"
          variant="alert"
          title={t('expenseCategories.mapSync.missingSyncMode')}
        />
      )}
    </Modal>
  );
};
